/* ============================================================
 * 归途 · Homeward —— 静态数据表（装备 / 技能 / 境界 / 兑换）
 * 这里是纯数据定义，不依赖 DOM 与游戏状态，方便前后端共享或复用到工具脚本。
 * ============================================================ */

export const SLOT_DEFS = {
  helmet: { label: '头盔', icon: '盔', base: ['粗布帽', '皮帽', '铁盔', '战盔'] },
  weapon: { label: '武器', icon: '武', base: [] },
  clothes: { label: '衣服', icon: '衣', base: ['布衣', '皮甲', '锁甲', '战袍'] },
  shoes: { label: '鞋子', icon: '靴', base: ['布靴', '皮靴', '铁靴', '疾行靴'] },
  gloves: { label: '手套', icon: '手', base: ['布手套', '皮手套', '铁手套', '锐爪手套'] },
  jewelry: { label: '首饰', icon: '饰', base: ['木珠串', '铜环', '玉坠', '灵玉项链'] },
  treasure: { label: '灵宝', icon: '灵', base: ['灵宝'] }   // 金色洞府终点掉落，自带特殊属性
};

export const WEAPON_KINDS = [
  { name: '长弓', icon: '弓' },
  { name: '短剑', icon: '剑' },
  { name: '爪', icon: '爪' },
  { name: '法杖', icon: '杖' },
  { name: '盾牌', icon: '盾' }
];

export const RARITY_DEFS = {
  common: { name: '普通', color: '#d8d3c2', tier: 1 },
  magic: { name: '魔法', color: '#67b6ff', tier: 2 },
  rare: { name: '稀有', color: '#ffd265', tier: 3 },
  legend: { name: '传奇', color: '#ff9df0', tier: 4 }   // 灵宝专用品级
};

// 锁定商店商品所需金币：白色 10 / 蓝色 20 / 黄色 30
export const SHOP_LOCK_COST = { common: 10, magic: 20, rare: 30, legend: 50 };

/* ============================================================
 * 灵宝特殊属性（金色洞府终点掉落的传奇装备专用）
 *   这类属性不属于常规词缀：要么改机制，要么给特殊收益。
 *   kind: 'passive' 常驻特效 / 'skill' 附带主动技能
 * ============================================================ */
export const TREASURE_SPECIALS = [
  {
    key: 'strToHpDouble', kind: 'passive', name: '蛮力之心',
    desc: '每点力量增加的生命上限从 5 点提升到 10 点'
  },
  {
    key: 'alwaysFirst', kind: 'passive', name: '先手符诏',
    desc: '永远先手出手，不受移动速度影响'
  },
  {
    key: 'goldPerWin', kind: 'passive', name: '聚宝灵纹',
    desc: '每场战斗胜利时，必然额外获得 10 金币'
  },
  {
    key: 'hpFlat1000', kind: 'passive', name: '不坏金身',
    desc: '生命上限最大值提高 1000'
  },
  {
    key: 'grantSkill', kind: 'skill', name: '灵宝传承',
    desc: '附带一个强力主动技能（装备后自动学会）'
  },
  {
    key: 'lifesteal10', kind: 'passive', name: '血饮魔纹',
    desc: '每次攻击命中额外回复 5% 最大生命'
  },
  {
    key: 'magicAmp', kind: 'passive', name: '蓝宝灵韵',
    desc: '已装备的蓝色（魔法）装备属性增幅 50%~150%'
  },
  {
    // 稀有灵宝：出现几率远低于其它灵宝（见 pickTreasureSpecial 的权重）
    key: 'levelBoost', kind: 'passive', name: '破境符诏', rare: true,
    desc: '临时强行提升 5 个小境界（例如从「武者三境·中期」提到「武者五境·初期」）：挑战世界BOSS时按 +5 级计算境界门槛（其它地方不生效）'
  }
];

// 被动技能石可选的属性增幅（参考流放之路的辅助宝石思路）
export const GEM_OPTIONS = [
  { id: 'weaponDamage', name: '武器伤害提高', min: 15, max: 25, unit: '%' },
  { id: 'meleeDamage', name: '近战伤害提高', min: 18, max: 30, unit: '%' },
  { id: 'spellDamage', name: '法术伤害提高', min: 15, max: 25, unit: '%' },
  { id: 'summonDamage', name: '召唤伤害提高', min: 20, max: 35, unit: '%' },
  { id: 'life', name: '生命上限提高', min: 15, max: 30, unit: '' },
  { id: 'energy', name: '魔力上限提高', min: 8, max: 16, unit: '' },
  { id: 'moveSpeed', name: '移动速度提高', min: 3, max: 6, unit: '%' },
  { id: 'armorUp', name: '护甲提高', min: 10, max: 20, unit: '' },
  // 专属辅助：只能镶嵌到指定主动技能上（不给数值，而是改技能行为）
  {
    id: 'earthshaker', name: '震撼大地（辅助）', min: 0, max: 0, unit: '',
    support: true, onlySkill: 'yangClaw',
    short: '阳爪专属：脚步震裂大地，冲击波随机触发 1~5 次（每次伤害独立结算与显示）',
    desc: '仅可镶嵌到「阳爪」：施放阳爪时你的脚步震裂大地，冲击波随机触发 1~5 次，' +
      '每次冲击波的伤害单独计算、单独显示（对范围内所有敌人，每次都是「普通攻击伤害的 50% + 力量」）。'
  },
  // ---------- 投射物辅助（只能镶嵌到投射物技能，例：破空斩的剑气）----------
  {
    id: 'multipleProjectiles', name: '多重投射物（辅助）', min: 0, max: 0, unit: '',
    support: true, projectileOnly: true,
    short: '伤害总降 25%、技能速度总降 20%，但可以多发射 2 个投射物',
    desc: '辅助投射物技能，可以发射额外的投射物。同时降低被辅助技能的攻击和施法速度，并降低它们的伤害。' +
      '被辅助技能造成的伤害总降 25%；被辅助技能可以发射 2 个额外投射物；被辅助技能的技能速度总降 20%。'
  },
  {
    id: 'projectileSpeed', name: '投射物加速（辅助）', min: 0, max: 0, unit: '',
    support: true, projectileOnly: true,
    short: '被辅助技能的投射物速度总增 40%',
    desc: '辅助投射物技能，使投射物速度加快：被辅助技能的投射物速度总增 40%。'
  },
  // ---------- 通用击中辅助 ----------
  {
    id: 'execute', name: '处决（辅助）', min: 0, max: 0, unit: '',
    support: true,
    short: '你或敌人处于低血（≤50% 生命）时，被辅助技能的击中伤害总增 30%',
    desc: '辅助任意击中敌人的技能，使其对低血敌人造成更高伤害，当你低血时造成的伤害也更高。' +
      '在你低血（≤ 最大生命 50%）时，被辅助技能的击中伤害总增 30%；' +
      '被辅助技能对低血敌人（≤ 最大生命 50%）造成的击中伤害总增 30%。'
  },
  {
    id: 'flameLegion', name: '炎军（辅助）', min: 0, max: 0, unit: '',
    support: true,
    short: '造成伤害时 50% 几率叠加 1 层燃烧；每层每回合造成你最大生命 1% 的真实伤害',
    desc: '辅助任意击中敌人的技能：每次对敌人造成伤害时，有 50% 几率给敌人叠加 1 层燃烧效果。' +
      '每层燃烧在敌人每个回合承受你最大生命值 1% 的真实伤害（无视护甲与抗性），层数可以叠加（上限 20 层）。'
  }
];

// 精英挑战奖励的随机主动技能
export const SKILL_POOL = [
  { id: 'heal', name: '恢复术', type: 'heal', cost: 6, desc: '恢复 35% 最大生命。' },
  { id: 'fireball', name: '火球术', type: 'spell', cost: 8, dmg: [42, 60], desc: '投出火球，造成 42~60 点火焰伤害。' },
  { id: 'lightning', name: '雷电术', type: 'spell', cost: 7, dmg: [32, 48], desc: '召来雷电，造成 32~48 点闪电伤害。' },
  { id: 'summonEnemy', name: '召唤普通敌人', type: 'summon', cost: 6, desc: '召唤普通敌人为你作战 3 次攻击。' }
];

// 主动技能石可开出的主动技能池（minStage 控制境界限制）
export const ACTIVE_SKILL_POOL = [
  { id: 'airSlash', name: '破空斩', type: 'melee', cost: 8, dmg: [44, 62], minStage: 1, desc: '斩出破空剑气，造成 44~62 点伤害。' },
  {
    id: 'yangClaw', name: '阳爪', type: 'melee', cost: 9, dmg: [6, 10], minStage: 1,
    desc: '裂伤挥斩一名敌人：连续 6 次攻击，每次造成 6~10 点基础伤害（逐次独立结算与显示），' +
      '命中后使战场上的敌人陷入流血（每回合持续掉血）；与目标距离越远伤害越高，最多总增 100%。'
  },
  {
    id: 'bloodRebound',
    name: '气血反弹',
    type: 'toggle',
    cost: 5,
    minStage: 1,
    desc: '切换持续状态：每次被敌人攻击时，对敌人造成“自身最大生命 10% + 力量”的伤害（可重复激活/关闭）。'
  },
  { id: 'summonScorpion', name: '召唤蝎子', type: 'summon', cost: 5, minStage: 1, desc: '召唤沙漠毒蝎跟随你作战。' },
  { id: 'summonHyena', name: '召唤鬣狗', type: 'summon', cost: 5, minStage: 1, desc: '召唤鬣狗跟随你作战。' },
  { id: 'summonSnake', name: '召唤毒蛇', type: 'summon', cost: 5, minStage: 1, desc: '召唤沙漠毒蛇跟随你作战。' },
  { id: 'summonVulture', name: '召唤猎鹰', type: 'summon', cost: 5, minStage: 1, desc: '召唤猎鹰跟随你作战。' },
  { id: 'lightningArrow', name: '闪电箭', type: 'spell', cost: 7, dmg: [38, 55], minStage: 31, desc: '射出闪电箭，造成 38~55 点闪电伤害。' },
  { id: 'fireball', name: '火球术', type: 'spell', cost: 8, dmg: [42, 60], minStage: 31, desc: '投出火球，造成 42~60 点火焰伤害。' },
  { id: 'lightning', name: '雷电术', type: 'spell', cost: 7, dmg: [32, 48], minStage: 31, desc: '召来雷电，造成 32~48 点闪电伤害。' },
  { id: 'summonBats', name: '召唤蝙蝠', type: 'summon', cost: 7, minStage: 31, desc: '召唤蝙蝠群为你作战 3 次攻击。' }
];

// 《凡人修仙传》修行体系：武者十境 → 下境界 → 中境界 → 上境界
export const REALM_LIST = [
  '武者一境', '武者二境', '武者三境', '武者四境', '武者五境',
  '武者六境', '武者七境', '武者八境', '武者九境', '武者十境',
  '炼气', '筑基', '结丹', '元婴', '化神',
  '炼虚', '合体', '大乘',
  '真仙', '金仙', '太乙', '大罗（玉仙）', '道祖'
];

export const CULTIVATION_STAGES = [];
REALM_LIST.forEach(function (realm, ri) {
  const subs = realm === '道祖' ? [''] : ['初期', '中期', '后期'];
  subs.forEach(function (sub, si) {
    CULTIVATION_STAGES.push({ realm: realm, sub: sub, realmIndex: ri, subIndex: si });
  });
});

export const MAX_STAGE = CULTIVATION_STAGES.length;
export const TRIBULATION_STAGE = 30; // 武者十境后期（大圆满，需要天地灵物）

export const SAVE_KEY = 'homeward.save.v1';

/* ============================================================
 * 玩家属性点（自由分配）
 *   每提升一个境界（等级）获得 ATTR_POINTS_PER_LEVEL 点
 *   力量 / 敏捷 / 智力 的加成写在 note 里，角色窗口会原样展示
 * ============================================================ */
export const ATTR_POINTS_PER_LEVEL = 1;

export const ATTR_DEFS = [
  { id: 'str', label: '力量', note: '每点 +5 生命上限；力量总额还会加强「气血反弹」' },
  { id: 'agi', label: '敏捷', note: '每点 +1% 攻击速度、+0.6% 移动速度' },
  { id: 'int', label: '智力', note: '每点 +2 魔力上限（施放法术消耗魔力，魔力同时提高宠物伤害）' }
];

/* ============================================================
 * 马路上的遗迹洞府
 *   绿色 / 蓝色：随时可以进入与离开
 *   金色：需要「金色洞府令牌」（只能用活力兑换）才能进入
 *   敌人数量基准（野外一段路按 CAVE_WILD_BASE 个敌人算）：
 *     绿色 = 1.5 × 野外、蓝色 = 2 × 绿色、金色 = 2 × 蓝色
 * ============================================================ */
export const CAVE_WILD_BASE = 8;          // 野外基准敌人数量（仅用于说明比例）
export const CAVE_DEFS = [
  {
    id: 'green', name: '绿色洞府', color: '#5ce08a', glow: 'rgba(92, 224, 138, .55)',
    enemies: Math.round(CAVE_WILD_BASE * 1.5), // 12
    token: false, weight: 55,
    note: '随时可以进入，敌人密度比野外更高',
    tiers: ['common', 'common', 'magic']
  },
  {
    id: 'blue', name: '蓝色洞府', color: '#5fb0ff', glow: 'rgba(95, 176, 255, .55)',
    enemies: Math.round(CAVE_WILD_BASE * 1.5) * 2, // 24（绿色 ×2）
    token: false, weight: 33,
    note: '随时可以进入，敌人数量是绿色洞府的 2 倍',
    tiers: ['common', 'magic', 'magic', 'elite']
  },
  {
    id: 'gold', name: '金色洞府', color: '#ffd76a', glow: 'rgba(255, 215, 106, .6)',
    enemies: Math.round(CAVE_WILD_BASE * 1.5) * 2 * 2, // 48（蓝色 ×2）
    token: true, weight: 12,
    note: '需要金色洞府令牌才能进入，敌人数量是蓝色洞府的 2 倍',
    tiers: ['magic', 'elite', 'elite', 'legend']
  }
];
export const CAVE_TOKEN_COST = 10;        // 活力兑换金色洞府令牌所需活力（每次进金色洞府消耗 1 枚）
// 洞府里战斗时，附近敌人加入战斗（多对一）的几率
export const CAVE_JOIN_CHANCE = 0.35;
export const CAVE_JOIN_MAX = 3;           // 一场战斗最多同时面对几个敌人

/* ============================================================
 * 图鉴阶级：普通 / 魔法 / 稀有 / 传奇 / 暗金
 *   rate    单抽出现几率（%）
 *   rateTen 十连每一抽的出现几率（%）：稀有 / 传奇 / 暗金 都被抬高
 *   comp    抽到重复藏品时的金币补偿
 * ============================================================ */
export const CODEX_TIERS = [
  {
    id: 'common', name: '普通', color: '#d8d3c2', rate: 58.52, rateTen: 61.00, comp: 20,
    note: '路边摊随手就能收来的凡品'
  },
  {
    id: 'magic', name: '魔法', color: '#67b6ff', rate: 36.38, rateTen: 37.95, comp: 50,
    note: '带一点灵性的小玩意'
  },
  {
    id: 'rare', name: '稀有', color: '#ffd265', rate: 5, rateTen: 1, comp: 120,
    note: '难得一见的地方名物'
  },
  {
    id: 'legend', name: '传奇', color: '#ff8f4d', rate: 0.1, rateTen: 0.05, comp: 400,
    note: '史册里留有名字的宝物'
  },
  {
    // 暗金：不参与抽卡，也不会被保底抽出，只能靠活动等其它方式获得（暂未开放）
    id: 'unique', name: '暗金', color: '#c58bff', rate: 0, rateTen: 0, comp: 1200,
    gacha: false,
    source: '活动等方式获取（暂未开放）',
    note: '世上仅此一件，得之即为传承'
  }
];

/* ============================================================
 * 图鉴藏品（收集后提供永久增幅属性）
 *   tier: common / magic / rare / legend / unique
 *   bonus: 属性键 -> 数值，键名与装备词缀一致
 * ============================================================ */
export const CODEX_ITEMS = [
  // ---------- 普通 ----------
  {
    id: 'cm_pot', tier: 'common', name: '粗陶护身符', icon: '符', type: '饰品',
    desc: '村口陶窑烧出的护身符，据说能挡一次风寒。',
    bonus: { hp: 8 }
  },
  {
    id: 'cm_hammer', tier: 'common', name: '铁匠锤', icon: '锤', type: '武器',
    desc: '抡起来砸核桃比砸人顺手。',
    bonus: { atk: 3 }
  },
  {
    id: 'cm_hat', tier: 'common', name: '猎户皮帽', icon: '帽', type: '头盔',
    desc: '猎户进山前都会戴上一顶。',
    bonus: { armor: 5 }
  },
  {
    id: 'cm_wrap', tier: 'common', name: '粗布足袋', icon: '袋', type: '鞋子',
    desc: '缠上它，走在滚烫的沙上也稳当些。',
    bonus: { speedPct: 2 }
  },
  {
    id: 'cm_dagger', tier: 'common', name: '木柄短匕', icon: '匕', type: '武器',
    desc: '削木头、剔骨头都很称手。',
    bonus: { atk: 2, critChance: 1 }
  },
  {
    id: 'cm_bag', tier: 'common', name: '干粮布袋', icon: '粮', type: '道具',
    desc: '里头还剩半块硬饼。',
    bonus: { hp: 6, lifeRegen: 1 }
  },
  {
    id: 'cm_coins', tier: 'common', name: '铜钱串', icon: '钱', type: '饰品',
    desc: '外圆内方，压得住邪气。',
    bonus: { res: 3 }
  },
  {
    id: 'cm_basket', tier: 'common', name: '药农背篓', icon: '篓', type: '道具',
    desc: '背篓里总混着一股草药味。',
    bonus: { mp: 6 }
  },

  // ---------- 魔法 ----------
  {
    id: 'mg_jade', tier: 'magic', name: '青纹玉佩', icon: '玉', type: '饰品',
    desc: '玉纹如水，贴着心口时微微发凉。',
    bonus: { hp: 18, res: 4 }
  },
  {
    id: 'mg_bow', tier: 'magic', name: '游侠轻弓', icon: '弓', type: '武器',
    desc: '弓身轻若无物，开弦却劲道十足。',
    bonus: { atk: 6, atkSpeed: 4 }
  },
  {
    id: 'mg_bracer', tier: 'magic', name: '寒铁护腕', icon: '腕', type: '护腕',
    desc: '铁面结霜，碰一下能划破皮。',
    bonus: { armor: 18, thorns: 6 }
  },
  {
    id: 'mg_staff', tier: 'magic', name: '聚灵木杖', icon: '杖', type: '武器',
    desc: '杖头聚着一小团不散的水汽。',
    bonus: { mp: 16, critChance: 2 }
  },
  {
    id: 'mg_boots', tier: 'magic', name: '疾风布靴', icon: '靴', type: '鞋子',
    desc: '落地极轻，走夜路不惊犬。',
    bonus: { speedPct: 4, hp: 12 }
  },
  {
    id: 'mg_glove', tier: 'magic', name: '铜环护手', icon: '手', type: '手套',
    desc: '环上刻着谁也不认得的古符。',
    bonus: { atk: 4, atkSpeed: 3 }
  },

  // ---------- 稀有 ----------
  {
    id: 'rr_helm', tier: 'rare', name: '赤炎战盔', icon: '盔', type: '头盔',
    desc: '盔顶余温未散，像刚离炉膛。',
    bonus: { hp: 30, armor: 25 }
  },
  {
    id: 'rr_ring', tier: 'rare', name: '幽蓝蛇戒', icon: '戒', type: '饰品',
    desc: '戒面盘着一条会眨眼的青蛇。',
    bonus: { critChance: 4, critDamage: 15 }
  },
  {
    id: 'rr_charm', tier: 'rare', name: '玄水石符', icon: '符', type: '饰品',
    desc: '浸在井水里三天三夜也不会湿。',
    bonus: { res: 6, chaosRes: 4 }
  },
  {
    id: 'rr_sword', tier: 'rare', name: '断岳长剑', icon: '剑', type: '武器',
    desc: '剑脊有道旧裂痕，是从前劈山留下的。',
    bonus: { atk: 10, critDamage: 12 }
  },
  {
    id: 'rr_mail', tier: 'rare', name: '荆棘重甲', icon: '甲', type: '衣服',
    desc: '甲面生满倒刺，抱一下便知厉害。',
    bonus: { thorns: 14, armor: 30 }
  },
  {
    id: 'rr_boots', tier: 'rare', name: '疾影长靴', icon: '靴', type: '鞋子',
    desc: '靴底磨得极薄，却从未穿透过。',
    bonus: { speedPct: 7, atkSpeed: 6 }
  },

  // ---------- 传奇 ----------
  {
    id: 'leg_mirror', tier: 'legend', name: '天罡护心镜', icon: '镜', type: '护甲',
    desc: '镜面映出杀意，可挡下最致命的一击。',
    bonus: { armor: 60, blockPct: 6 }
  },
  {
    id: 'leg_boots', tier: 'legend', name: '玄铁不灭靴', icon: '靴', type: '鞋子',
    desc: '踏沙不留痕，行过万里而不损分毫。',
    bonus: { speedPct: 8, hp: 30 }
  },
  {
    id: 'leg_pearl', tier: 'legend', name: '紫霄雷珠', icon: '珠', type: '法宝',
    desc: '珠中藏着一道未落下的天雷。',
    bonus: { critChance: 8, critDamage: 25 }
  },
  {
    id: 'leg_gloves', tier: 'legend', name: '金刚伏魔手', icon: '手', type: '手套',
    desc: '据说是降魔者以自身骨血炼成的手套。',
    bonus: { atk: 8, atkSpeed: 10 }
  },
  {
    id: 'leg_robe', tier: 'legend', name: '万木归元甲', icon: '甲', type: '衣服',
    desc: '以万年灵木编织，生机不绝。',
    bonus: { hp: 70, armor: 40 }
  },
  {
    id: 'leg_ring', tier: 'legend', name: '星辰碎影戒', icon: '戒', type: '饰品',
    desc: '戴上它，连星辰的坠落都慢了一瞬。',
    bonus: { critReduce: 12, res: 5 }
  },
  {
    id: 'leg_bracer', tier: 'legend', name: '血魂护腕', icon: '腕', type: '护腕',
    desc: '护腕上凝着历代主人的血魂，反噬一切来犯者。',
    bonus: { thorns: 18, strength: 15 }
  },

  // ---------- 暗金 ----------
  {
    id: 'uni_sword', tier: 'unique', name: '太虚斩仙剑', icon: '剑', type: '武器',
    desc: '传说中斩落过仙人的一剑，锋芒所指，虚空生裂。',
    bonus: { atk: 14, critChance: 4 }
  },
  {
    id: 'uni_banner', tier: 'unique', name: '混沌吞天幡', icon: '幡', type: '法宝',
    desc: '幡面漆黑如夜，能吞噬一切元素之力。',
    bonus: { res: 8, chaosRes: 6, mp: 20 }
  },
  {
    id: 'uni_jade', tier: 'unique', name: '九幽血玉', icon: '玉', type: '饰品',
    desc: '九幽深处的血玉，握之如握一颗跳动的心脏。',
    bonus: { hp: 90, lifeRegen: 5 }
  },
  {
    id: 'uni_dragon', tier: 'unique', name: '烛龙逆鳞甲', icon: '甲', type: '衣服',
    desc: '以烛龙逆鳞串成，触之如触滚烫的岩浆。',
    bonus: { hp: 120, armor: 80, thorns: 25 }
  },
  {
    id: 'uni_seal', tier: 'unique', name: '太一神雷印', icon: '印', type: '法宝',
    desc: '印面雷纹流转，落下时天地俱寂。',
    bonus: { critChance: 10, critDamage: 40, mp: 30 }
  }
];

// 老存档的图鉴 id 迁移（原「神级」并入「暗金」）
export const CODEX_LEGACY_IDS = {
  god_sword: 'uni_sword',
  god_banner: 'uni_banner',
  god_jade: 'uni_jade'
};

// 图鉴属性键 -> 中文名（用于面板显示）
export const STAT_LABELS = {
  hp: '最大生命', armor: '护甲', atk: '攻击伤害', speedPct: '移动速度', mp: '最大魔力',
  res: '全元素抗性', thorns: '荆棘伤害', blockPct: '格挡几率', strength: '力量',
  chaosRes: '混沌抗性', critReduce: '暴击伤害降低', armorPct: '护甲提高',
  lifeRegen: '生命每回合再生', atkSpeed: '攻击速度', critChance: '暴击率', critDamage: '暴击伤害'
};

/* ============================================================
 * 抽卡系统：消耗金币，按阶级概率抽取图鉴藏品
 *   单抽 / 十连 用两套概率表（CODEX_TIERS 的 rate / rateTen）：
 *   十连会整体抬高 稀有 / 传奇 / 暗金 的几率，并额外保底一件稀有及以上。
 * ============================================================ */
export const GACHA_SINGLE_COST = 500;   // 单抽金币
export const GACHA_TEN_COST = 5000;     // 十连金币
export const GACHA_PITY_MAX = 50;       // 50 抽内必出传奇（暗金不参与保底）
export const GACHA_TEN_MIN_TIER = 'rare'; // 十连保底的最低阶级

// 十连限流：一次十连里 稀有 最多 2 件，传奇 + 暗金 合计最多 1 件
export const GACHA_TEN_CAPS = { rare: 2, legend: 1 };

/* ============================================================
 * 装备词缀数值区间（按 10 级为一档）
 *
 * 规则：装备等级决定"能出现的最高档位"，但每次都从 0 档开始随机。
 *   1~10 级装备：只能出第 1 档（1-10 级区间）
 *   15 级装备   ：可以出第 1 档或第 2 档（1-20 级区间）
 *   100 级装备  ：可以出第 1~10 档（也可能六条全是最低档 → 看运气）
 * ============================================================ */
export const AFFIX_BAND_SIZE = 10;

// 盾牌专属属性库（参考 PoE2：生命 / 护甲 / 荆棘 / 格挡 / 力量 / 元素抗性 / 混沌抗性 / 暴击伤害降低）
export const SHIELD_AFFIXES = [
  {
    key: 'hp',
    label: '最大生命 +{v}',
    bands: [[5, 12], [12, 22], [22, 35], [35, 50], [50, 70], [70, 95], [95, 125], [125, 160], [160, 200], [200, 260]]
  },
  {
    key: 'armor',
    label: '护甲 +{v}',
    bands: [[6, 14], [14, 26], [26, 42], [42, 62], [62, 88], [88, 120], [120, 160], [160, 210], [210, 270], [270, 340]]
  },
  {
    key: 'thorns',
    label: '荆棘伤害 +{v}',
    bands: [[2, 5], [5, 9], [9, 14], [14, 20], [20, 28], [28, 38], [38, 50], [50, 65], [65, 82], [82, 105]]
  },
  {
    key: 'blockPct',
    label: '格挡几率 +{v}%',
    bands: [[2, 4], [3, 6], [5, 8], [6, 10], [8, 12], [10, 14], [12, 17], [14, 20], [17, 24], [20, 28]]
  },
  {
    key: 'strength',
    label: '力量 +{v}',
    bands: [[3, 7], [6, 12], [10, 18], [15, 25], [20, 32], [26, 40], [32, 48], [40, 58], [48, 70], [58, 85]]
  },
  {
    key: 'res',
    label: '全元素抗性 +{v}%',
    bands: [[2, 5], [4, 8], [6, 11], [8, 14], [10, 17], [12, 20], [14, 24], [16, 28], [18, 32], [20, 36]]
  },
  {
    key: 'chaosRes',
    label: '混沌抗性 +{v}%',
    bands: [[1, 3], [2, 5], [3, 7], [4, 9], [5, 11], [6, 13], [7, 15], [8, 17], [10, 20], [11, 23]]
  },
  {
    key: 'critReduce',
    label: '暴击伤害降低 {v}%',
    bands: [[2, 4], [3, 6], [5, 8], [6, 10], [8, 12], [10, 15], [12, 18], [14, 21], [16, 24], [18, 28]]
  }
];

// 其他部位/武器共用的通用词缀（数值仍按等级成长）
export const GENERIC_AFFIXES = [
  { key: 'hp', label: '最大生命 +{v}' },
  { key: 'armor', label: '护甲 +{v}' },
  { key: 'atk', label: '攻击伤害 +{v}' },
  { key: 'speedPct', label: '移动速度提高 {v}%' },
  { key: 'mp', label: '最大魔力 +{v}' },
  { key: 'res', label: '全元素抗性 +{v}%' },
  { key: 'thorns', label: '荆棘伤害 +{v}' }
];

/* ============================================================
 * 头盔 / 衣服 共用词缀库（参考 PoE2 防具词缀）
 *   - hybridArmor：复合词缀「护甲提高 X%，生命上限 +Y」，占 1 条词缀但给两项数值
 * ============================================================ */
export const ARMOUR_AFFIXES = [
  {
    key: 'hp',
    label: '最大生命 +{v}',
    bands: [[5, 12], [12, 22], [22, 35], [35, 50], [50, 70], [70, 95], [95, 125], [125, 160], [160, 200], [200, 260]]
  },
  {
    key: 'armor',
    label: '护甲 +{v}',
    bands: [[6, 14], [14, 26], [26, 42], [42, 62], [62, 88], [88, 120], [120, 160], [160, 210], [210, 270], [270, 340]]
  },
  {
    key: 'hybridArmor',
    label: '护甲提高 {v}%，生命上限 +{v2}',
    bands: [[5, 10], [8, 16], [12, 22], [16, 28], [20, 35], [24, 42], [28, 50], [32, 58], [38, 68], [44, 80]],
    bands2: [[4, 10], [10, 18], [18, 28], [28, 40], [40, 56], [56, 76], [76, 100], [100, 128], [128, 160], [160, 210]]
  },
  {
    key: 'thorns',
    label: '荆棘伤害 +{v}',
    bands: [[2, 5], [5, 9], [9, 14], [14, 20], [20, 28], [28, 38], [38, 50], [50, 65], [65, 82], [82, 105]]
  },
  {
    key: 'strength',
    label: '力量 +{v}',
    bands: [[3, 7], [6, 12], [10, 18], [15, 25], [20, 32], [26, 40], [32, 48], [40, 58], [48, 70], [58, 85]]
  },
  {
    key: 'chaosRes',
    label: '混沌抗性 +{v}%',
    bands: [[1, 3], [2, 5], [3, 7], [4, 9], [5, 11], [6, 13], [7, 15], [8, 17], [10, 20], [11, 23]]
  },
  {
    key: 'res',
    label: '全元素抗性 +{v}%',
    bands: [[2, 5], [4, 8], [6, 11], [8, 14], [10, 17], [12, 20], [14, 24], [16, 28], [18, 32], [20, 36]]
  },
  {
    key: 'lifeRegen',
    label: '生命每回合再生 +{v}',
    bands: [[1, 2], [2, 4], [3, 6], [4, 8], [6, 11], [8, 14], [10, 18], [12, 22], [15, 27], [18, 33]]
  }
];

// 手套：在防具词缀库基础上，额外增加攻击类词缀
// 鞋子：走"分档位"的词缀库（和盾牌/防具一样，等级只决定最高档位），
// 不含攻击伤害，避免高等级鞋子滚出几十上百点的攻击/荆棘数值。
export const SHOES_AFFIXES = [
  {
    key: 'speedPct',
    label: '移动速度提高 {v}%',
    bands: [[3, 5], [4, 6], [5, 8], [6, 9], [7, 11], [8, 13], [9, 15], [10, 15], [12, 15], [13, 15]]
  },
  // 三维属性：鞋子也能出力量 / 敏捷 / 智力（数值随装备等级档位提升）
  {
    key: 'strength',
    label: '力量 +{v}',
    bands: [[1, 3], [2, 5], [3, 8], [5, 11], [7, 15], [9, 19], [12, 24], [15, 30], [18, 37], [22, 45]]
  },
  {
    key: 'agility',
    label: '敏捷 +{v}',
    bands: [[1, 3], [2, 5], [3, 8], [5, 11], [7, 15], [9, 19], [12, 24], [15, 30], [18, 37], [22, 45]]
  },
  {
    key: 'intelligence',
    label: '智力 +{v}',
    bands: [[1, 3], [2, 5], [3, 8], [5, 11], [7, 15], [9, 19], [12, 24], [15, 30], [18, 37], [22, 45]]
  },
  {
    key: 'hp',
    label: '最大生命 +{v}',
    bands: [[5, 12], [12, 22], [22, 35], [35, 50], [50, 70], [70, 95], [95, 125], [125, 160], [160, 200], [200, 260]]
  },
  {
    key: 'armor',
    label: '护甲 +{v}',
    bands: [[6, 14], [14, 26], [26, 42], [42, 62], [62, 88], [88, 120], [120, 160], [160, 210], [210, 270], [270, 340]]
  },
  {
    key: 'mp',
    label: '最大魔力 +{v}',
    bands: [[4, 8], [8, 14], [14, 22], [22, 32], [32, 45], [45, 60], [60, 78], [78, 98], [98, 120], [120, 150]]
  },
  {
    key: 'res',
    label: '全元素抗性 +{v}%',
    bands: [[1, 3], [2, 5], [4, 7], [5, 9], [6, 11], [8, 13], [9, 16], [11, 18], [12, 21], [14, 24]]
  },
  {
    key: 'thorns',
    label: '荆棘伤害 +{v}',
    bands: [[2, 4], [4, 6], [5, 8], [7, 11], [9, 14], [12, 18], [15, 22], [19, 27], [23, 33], [28, 40]]
  }
];

export const GLOVES_AFFIXES = ARMOUR_AFFIXES.concat([
  {
    key: 'atk',
    label: '攻击伤害 +{v}',
    bands: [[2, 5], [4, 8], [6, 12], [9, 16], [12, 20], [16, 26], [20, 32], [25, 40], [30, 48], [36, 58]]
  },
  {
    key: 'atkSpeed',
    label: '攻击速度提高 {v}%',
    bands: [[2, 4], [3, 6], [5, 8], [6, 10], [8, 12], [10, 14], [12, 17], [14, 20], [17, 24], [20, 28]]
  },
  {
    key: 'critChance',
    label: '暴击率 +{v}%',
    bands: [[1, 2], [2, 4], [3, 5], [4, 6], [5, 8], [6, 9], [7, 11], [8, 12], [10, 14], [11, 16]]
  },
  {
    key: 'critDamage',
    label: '暴击伤害 +{v}%',
    bands: [[4, 8], [7, 14], [10, 20], [14, 26], [18, 33], [22, 40], [26, 48], [30, 56], [35, 65], [40, 75]]
  }
]);

/* ============================================================
 * 首饰词缀库（项链 / 戒指一类的饰品）
 *   参考 PoE：生命 / 三抗 / 混沌抗 / 属性 / 护甲 / 生命再生 / 魔力
 * ============================================================ */
export const JEWELRY_AFFIXES = [
  {
    key: 'hp',
    label: '最大生命 +{v}',
    bands: [[5, 12], [12, 22], [22, 35], [35, 50], [50, 70], [70, 95], [95, 125], [125, 160], [160, 200], [200, 260]]
  },
  {
    key: 'res',
    label: '全元素抗性 +{v}%',
    bands: [[2, 5], [4, 8], [6, 11], [8, 14], [10, 17], [12, 20], [14, 24], [16, 28], [18, 32], [20, 36]]
  },
  {
    key: 'chaosRes',
    label: '混沌抗性 +{v}%',
    bands: [[1, 3], [2, 5], [3, 7], [4, 9], [5, 11], [6, 13], [7, 15], [8, 17], [10, 20], [11, 23]]
  },
  {
    key: 'strength',
    label: '力量 +{v}',
    bands: [[3, 7], [6, 12], [10, 18], [15, 25], [20, 32], [26, 40], [32, 48], [40, 58], [48, 70], [58, 85]]
  },
  {
    key: 'armor',
    label: '护甲 +{v}',
    bands: [[6, 14], [14, 26], [26, 42], [42, 62], [62, 88], [88, 120], [120, 160], [160, 210], [210, 270], [270, 340]]
  },
  {
    key: 'lifeRegen',
    label: '生命每回合再生 +{v}',
    bands: [[1, 2], [2, 4], [3, 6], [4, 8], [6, 11], [8, 14], [10, 18], [12, 22], [15, 27], [18, 33]]
  },
  {
    key: 'mp',
    label: '最大魔力 +{v}',
    bands: [[3, 8], [6, 14], [10, 22], [15, 32], [20, 44], [26, 58], [32, 72], [40, 90], [48, 110], [58, 132]]
  }
];

// ============================================================
// 世界BOSS
// · 目前只开放一个：「森特欧斯萨满」（等级 30 · 赤沙祭坛）
// · refreshMin = 刷新周期（分钟）：刷新后可挑战 3 分钟，没被击杀就自己消失，
//   剩下的时间进入冷却，到点再次刷新。
// · 后期联网后，这里会改成"全服共享血量 + 伤害排行榜结算"。
// ============================================================
export const WORLD_BOSSES = [
  {
    id: 'wb10', level: 10, name: '森特欧斯萨满', place: '赤沙祭坛',
    refreshMin: 0, open: true, sprite: 'yaoshi', hp: 7600, scale: 2.6, element: '血沙',
    // 鼠标停在列表这一行时弹出的资料图（每个BOSS可以各配一张，没配的不弹）
    info: 'assets/img/boss/stos_info.png',
    infoDetail: '赤沙祭坛的萨满祭司 · 用血沙献祭整片沙漠的游魂'
  },
  {
    id: 'wb25', level: 25, name: '流沙游魂·卡兹尔', place: '流沙深渊',
    refreshMin: 10, open: false, sprite: 'qigai', hp: 2600, scale: 2.2, element: '沙'
  },
  {
    id: 'wb40', level: 40, name: '血月祭司·瓦洛', place: '血月峡谷',
    refreshMin: 12, open: false, sprite: 'youmin', hp: 9800, scale: 2.6, element: '血月'
  },
  {
    id: 'wb52', level: 52, name: '荒骨巨灵', place: '白骨绿洲',
    refreshMin: 12, open: false, sprite: 'shanzhu', hp: 12400, scale: 3.0, element: '荒骨'
  },
  {
    id: 'wb61', level: 61, name: '赤沙魔君·伊赫', place: '焚风石林',
    refreshMin: 15, open: false, sprite: 'bingfei', hp: 15600, scale: 3.0, element: '焚风'
  },
  {
    id: 'wb67', level: 67, name: '万魂沙皇', place: '万魂沙海',
    refreshMin: 15, open: false, sprite: 'shuifei', hp: 18800, scale: 3.2, element: '万魂'
  }
];
